import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Input } from '#/components/ui/input'
import { Progress } from '#/components/ui/progress'
import { ChoiceCard } from '#/components/plan/choice-card'
import { api } from '#/lib/api'
import { sendStoredAttribution } from '#/lib/api-misc'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'
import { Briefcase, Code2, GraduationCap, Rocket, Search, Users, Youtube, Instagram, Twitter, MoreHorizontal } from 'lucide-react'

export const Route = createFileRoute('/onboarding')({ validateSearch: z.object({ redirect: z.string().optional() }), component: Onboarding })

/** 3-step onboarding (spec/01 §12). Guests → sign-in; already-onboarded users → straight to `redirect` (default `/home`). */
function Onboarding() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const target = (redirect && redirect.startsWith('/') ? redirect : '/home') as '/home'
  const { data: session, isPending } = authClient.useSession()
  const user = session?.user as { id: string; name: string; email: string; onboardedAt?: string | Date | null } | undefined
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [source, setSource] = useState<string>('')
  const [ctx, setCtx] = useState<string>('')
  const attributed = useRef(false)
  useEffect(() => {
    if (isPending) return
    if (!user) {
      navigate({ to: '/sign-in', search: { redirect: `/onboarding${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}` } })
      return
    }
    if (user.onboardedAt) {
      navigate({ to: target, replace: true })
      return
    }
    if (!name && user.name) setName(user.name)
    if (!attributed.current) {
      attributed.current = true
      void sendStoredAttribution()
    }
  }, [isPending, user, navigate, target, redirect, name])
  const finish = async (skipped = false) => {
    await api('/api/user/onboarding', { method: 'POST', json: skipped ? { skipped: true } : { name, referralSource: source, context: ctx } }).catch(() => {})
    // better-auth caches the user in the session cookie (5 min); bypass it so `onboardedAt` is fresh on the next page.
    await authClient.getSession({ query: { disableCookieCache: true } }).catch(() => {})
    navigate({ to: target, replace: true })
  }
  const sources = [['youtube', m.onb_2_youtube(), <Youtube key="y" className="size-5" />], ['instagram', m.onb_2_instagram(), <Instagram key="i" className="size-5" />], ['twitter', m.onb_2_twitter(), <Twitter key="t" className="size-5" />], ['friend', m.onb_2_friend(), <Users key="f" className="size-5" />], ['google', m.onb_2_google(), <Search key="g" className="size-5" />], ['other', m.onb_2_other(), <MoreHorizontal key="o" className="size-5" />]] as const
  const ctxs = [['founder', m.onb_3_founder(), <Rocket key="r" className="size-5" />], ['dev', m.onb_3_dev(), <Code2 key="c" className="size-5" />], ['pm', m.onb_3_pm(), <Briefcase key="b" className="size-5" />], ['student', m.onb_3_student(), <GraduationCap key="s" className="size-5" />]] as const
  if (isPending || !user || user.onboardedAt) {
    return (
      <>
        <AppHeader showStats={false} />
        <main className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">{m.onb_redirecting()}</main>
      </>
    )
  }
  return (
    <>
      <AppHeader showStats={false} />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-xl space-y-6" data-testid="onboarding">
          <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{m.onb_step({ n: step, total: 3 })}</span><button onClick={() => finish(true)} className="hover:text-foreground" data-testid="onb-skip">{m.onb_skip()}</button></div>
          <Progress value={(step / 3) * 100} className="h-1.5" />
          {step === 1 ? (
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{m.onb_1_title()}</h1>
              <p className="text-muted-foreground">{m.onb_1_subtitle()}</p>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.onb_1_name()} className="h-11" data-testid="onb-name" />
              <Button className="h-11" disabled={!name.trim()} onClick={() => setStep(2)} data-testid="onb-next">{m.common_next()}</Button>
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{m.onb_2_title()}</h1>
              <div className="grid gap-3 sm:grid-cols-2">{sources.map(([k, label, icon]) => <ChoiceCard key={k} icon={icon} title={label} selected={source === k} onClick={() => { setSource(k); setStep(3) }} />)}</div>
            </div>
          ) : (
            <div className="space-y-4">
              <h1 className="text-3xl font-bold">{m.onb_3_title()}</h1>
              <div className="grid gap-3 sm:grid-cols-2">{ctxs.map(([k, label, icon]) => <ChoiceCard key={k} icon={icon} title={label} selected={ctx === k} onClick={() => setCtx(k)} />)}</div>
              <Button className="h-11" disabled={!ctx} onClick={() => finish()} data-testid="onb-finish">{m.onb_finish()}</Button>
            </div>
          )}
        </div>
      </main>
    </>
  )
}
